import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';

const GameContext = createContext({});

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const { user, isAuthenticated, hasCharacter, selectedCharacter } = useAuth();
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const moveThrottleRef = useRef(0);          // timestamp of last move emit
  const refreshTimerRef = useRef(null);        // debounce timer for refreshCharacterData
  const refreshInFlightRef = useRef(false);    // prevent overlapping refresh fetches
  const [gameState, setGameState] = useState({
    character: null,
    area: null,
    maze: null,
    nearbyPlayers: [],
    onlinePlayers: [],
    chatMessages: [],
    isConnected: false,
    fellowship: null,
    fellowshipBattle: null,
    fellowshipInvite: null,
  });

  const [chatType, setChatType] = useState('global');
  const gameStateTimerRef = useRef(null);      // timeout for game_state retry

  useEffect(() => {
    if (isAuthenticated && hasCharacter) {
      connectToGame();
    } else if (socketRef.current) {
      disconnectFromGame();
    }

    return () => {
      if (gameStateTimerRef.current) {
        clearTimeout(gameStateTimerRef.current);
        gameStateTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, hasCharacter, selectedCharacter]); // eslint-disable-line react-hooks/exhaustive-deps

  const connectToGame = () => {
    const token = localStorage.getItem('token');
    console.log('Connecting to game with token:', token ? 'present' : 'missing');
    if (!token) return;

    // Guard against double-connection (e.g. React strict mode)
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const newSocket = io(process.env.REACT_APP_SERVER_URL || '', {
      auth: {
        token
      },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    newSocket.on('connect', () => {
      console.log('Connected to game server');
      setGameState(prev => ({ ...prev, isConnected: true }));
      // Safety net: if game_state not received within 5s, re-request it
      if (gameStateTimerRef.current) clearTimeout(gameStateTimerRef.current);
      gameStateTimerRef.current = setTimeout(() => {
        if (newSocket.connected) {
          console.log('game_state not received after 5s — requesting...');
          newSocket.emit('request_game_state');
        }
      }, 5000);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Connection error:', error.message);
      setGameState(prev => ({ ...prev, isConnected: false }));
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from game server');
      setGameState(prev => ({ ...prev, isConnected: false }));
      if (gameStateTimerRef.current) {
        clearTimeout(gameStateTimerRef.current);
        gameStateTimerRef.current = null;
      }
    });

    // Auto-reconnect: re-request game state so the player is synced
    newSocket.io.on('reconnect', () => {
      console.log('Reconnected to game server — requesting game state');
      setGameState(prev => ({ ...prev, isConnected: true }));
      newSocket.emit('request_game_state');
    });

    newSocket.on('game_state', (state) => {
      // Cancel any pending game_state retry timer
      if (gameStateTimerRef.current) {
        clearTimeout(gameStateTimerRef.current);
        gameStateTimerRef.current = null;
      }
      // Cancel any pending refreshCharacterData — but ONLY when the game_state
      // carries full character data (has inventory).  When the server uses its
      // "slim payload" optimisation (omitting inventory/seeds/skill_tree for
      // unchanged blobs), a pending REST refresh may still be needed to pick up
      // new data such as loot after battle.  (Bug #242)
      if (refreshTimerRef.current && state.character && 'inventory' in state.character) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      // Normalize character data — ensure computed fields always exist
      const char = state.character;
      if (char) {
        if (char.currentHp === undefined) char.currentHp = (char.max_hp || 0) - (char.damage || 0);
        if (char.currentMp === undefined) char.currentMp = (char.max_mp || 0) - (char.used_mp || 0);
      }
      let inventoryMissing = false;
      setGameState(prev => {
        const merged = prev.character ? { ...prev.character, ...char } : char;
        // Edge-case guard: if a slim payload arrived when there was no previous
        // character data to merge inventory from, the merged object will lack
        // inventory.  The server-side fix (Bug #245) should prevent this, but
        // we guard defensively so items are never silently lost.  (Bug #242)
        if (merged && !merged.inventory) inventoryMissing = true;
        return {
          ...prev,
          // Merge character: server may omit unchanged heavy blobs (inventory/seeds/skill_tree)
          // on area transitions to save bandwidth. Merge with previous state to preserve them.
          character: merged,
          area: state.area, // Keep for backward compatibility
          maze: state.maze, // New maze data
          nearbyPlayers: state.nearbyPlayers,
          questSummary: state.questSummary || null,
          dailyLoginReward: state.dailyLoginReward || prev.dailyLoginReward || null,
          welcomeBack: state.welcomeBack || prev.welcomeBack || null,
        };
      });
      // If inventory ended up missing after the merge, schedule a full REST
      // refresh so the player's items are recovered promptly.
      if (inventoryMissing) _doRefreshCharacter();
    });

    newSocket.on('player_moved', (playerData) => {
      setGameState(prev => ({
        ...prev,
        nearbyPlayers: prev.nearbyPlayers.map(player =>
          player.name === playerData.characterName
            ? { ...player, x_position: playerData.x, y_position: playerData.y, direction: playerData.direction }
            : player
        )
      }));
    });

    // Lightweight position update — sent on normal moves instead of full game_state
    newSocket.on('position_update', (data) => {
      setGameState(prev => {
        const char = prev.character;
        if (!char) return prev;
        return {
          ...prev,
          character: {
            ...char,
            x_position: data.x,
            y_position: data.y,
            direction: data.direction
          },
          nearbyPlayers: data.nearbyPlayers || prev.nearbyPlayers
        };
      });
    });

    // Lightweight character stat update — sent after heals/actions instead of full game_state
    newSocket.on('character_update', (data) => {
      setGameState(prev => {
        const char = prev.character;
        if (!char) return prev;
        return {
          ...prev,
          character: { ...char, ...data }
        };
      });
    });

    newSocket.on('player_joined', (playerData) => {
      setGameState(prev => ({
        ...prev,
        nearbyPlayers: [...prev.nearbyPlayers.filter(p => p.name !== playerData.characterName), {
          name: playerData.characterName,
          level: playerData.level,
          class: playerData.class,
          x_position: playerData.x,
          y_position: playerData.y,
          direction: playerData.direction
        }]
      }));
    });

    newSocket.on('player_left', (playerData) => {
      setGameState(prev => ({
        ...prev,
        nearbyPlayers: prev.nearbyPlayers.filter(p => p.name !== playerData.characterName)
      }));
    });

    // Online players list (updated when players join/leave)
    newSocket.on('online_players', (players) => {
      setGameState(prev => ({ ...prev, onlinePlayers: players || [] }));
    });

    // ── Fellowship events ──────────────────────────────────────────
    newSocket.on('fellowship_state', (fellowshipState) => {
      setGameState(prev => ({ ...prev, fellowship: fellowshipState }));
    });

    newSocket.on('fellowship_created', () => {
      // State will come via fellowship_state
    });

    newSocket.on('fellowship_disbanded', () => {
      setGameState(prev => ({ ...prev, fellowship: null, fellowshipBattle: null }));
    });

    newSocket.on('fellowship_invite', (invite) => {
      setGameState(prev => ({ ...prev, fellowshipInvite: invite }));
    });

    newSocket.on('fellowship_error', (data) => {
      console.warn('[Fellowship]', data?.message);
      // Show as system chat message
      setGameState(prev => ({
        ...prev,
        chatMessages: [...prev.chatMessages.slice(-99), {
          characterName: 'System',
          message: data?.message || 'Fellowship error',
          chatType: 'system',
          timestamp: new Date().toISOString()
        }]
      }));
    });

    newSocket.on('fellowship_info', (data) => {
      setGameState(prev => ({
        ...prev,
        chatMessages: [...prev.chatMessages.slice(-99), {
          characterName: 'System',
          message: data?.message || '',
          chatType: 'system',
          timestamp: new Date().toISOString()
        }]
      }));
    });

    // Fellowship combat events
    newSocket.on('fellowship_battle_started', (battleState) => {
      setGameState(prev => ({ ...prev, fellowshipBattle: battleState }));
    });

    newSocket.on('fellowship_battle_update', (battleState) => {
      setGameState(prev => ({ ...prev, fellowshipBattle: battleState }));
    });

    newSocket.on('fellowship_battle_ended', (battleState) => {
      setGameState(prev => ({ ...prev, fellowshipBattle: battleState }));
      // Clear after a short delay — PostBattleScreen handles the actual reward display.
      // The fellowship interface just shows the victory/defeat banner briefly.
      setTimeout(() => {
        setGameState(prev => ({ ...prev, fellowshipBattle: null }));
      }, 2000);
    });

    newSocket.on('fellowship_turn_timer', (data) => {
      setGameState(prev => {
        if (!prev.fellowshipBattle) return prev;
        return {
          ...prev,
          fellowshipBattle: { ...prev.fellowshipBattle, turnTimeRemaining: data.remaining }
        };
      });
    });

    newSocket.on('chat_message', (messageData) => {
      setGameState(prev => {
        // Deduplicate: skip if we already have this exact message
        const isDupe = prev.chatMessages.some(m =>
          m.timestamp === messageData.timestamp &&
          m.characterName === messageData.characterName &&
          m.message === messageData.message &&
          m.chatType === messageData.chatType
        );
        if (isDupe) return prev;
        return {
          ...prev,
          chatMessages: [...prev.chatMessages.slice(-99), messageData]
        };
      });
    });

    // Load chat history (recent messages from before we connected)
    newSocket.on('chat_history', (messages) => {
      setGameState(prev => {
        // Prepend history before any live messages, deduplicating
        const existing = new Set(prev.chatMessages.map(m => `${m.characterName}|${m.message}|${m.timestamp}`));
        const newMsgs = messages.filter(m => !existing.has(`${m.characterName}|${m.message}|${m.timestamp}`));
        return {
          ...prev,
          chatMessages: [...newMsgs, ...prev.chatMessages].slice(-100)
        };
      });
    });

    // Rate-limit feedback from server (Phase 1 safety). Surface as a
    // system chat message so players see the throttle instead of a silent drop.
    newSocket.on('rate_limited', (info) => {
      const secs = info?.retryAfterMs ? Math.ceil(info.retryAfterMs / 1000) : 2;
      const kind = info?.kind === 'chat' ? 'chat' : 'action';
      setGameState(prev => ({
        ...prev,
        chatMessages: [...prev.chatMessages.slice(-99), {
          characterName: 'System',
          message: `Slow down — try again in ~${secs}s (${kind} rate limit).`,
          chatType: 'system',
          type: 'system',
          timestamp: new Date(),
        }]
      }));
    });

    // Phase 3 — moderator forced disconnect (kick / account ban).
    // Show a full-page blocking message and wipe the token so the
    // client can't silently reconnect.
    newSocket.on('force_disconnect', (info) => {
      try {
        const reason = info?.reason || 'You have been disconnected by a moderator.';
        const code = info?.code || 'kicked';
        // Stash so LoginPage (or a router-level guard) can show it
        try { localStorage.setItem('force_disconnect_reason', JSON.stringify({ reason, code, at: Date.now() })); } catch { /* noop */ }
        if (code === 'banned') {
          try { localStorage.removeItem('token'); } catch { /* noop */ }
        }
        // Use a modal-style alert; keep it simple to avoid coupling
        // to the toast stack here.
        setTimeout(() => {
          try { window.alert(reason); } catch { /* noop */ }
          try { window.location.href = '/login'; } catch { /* noop */ }
        }, 50);
      } catch (e) {
        console.error('force_disconnect handler failed:', e);
      }
    });

    // See other players' actions (casting spells, etc.) in the game log
    newSocket.on('player_action', (actionData) => {
      console.log('Player action:', actionData);
      // Inject as a system chat message so it shows in the game log
      setGameState(prev => ({
        ...prev,
        chatMessages: [...prev.chatMessages.slice(-99), {
          characterName: actionData.characterName,
          message: actionData.message,
          chatType: 'action',
          timestamp: new Date()
        }]
      }));
    });

    // Notification when another player targets you
    newSocket.on('targeted_by_player', (data) => {
      console.log(`Targeted by ${data.sourceCharacterName}`);
      setGameState(prev => ({
        ...prev,
        chatMessages: [...prev.chatMessages.slice(-99), {
          characterName: 'System',
          message: `${data.sourceCharacterName} is targeting you!`,
          chatType: 'system',
          timestamp: new Date()
        }]
      }));
    });

    newSocket.on('action_result', (result) => {
      console.log('Action result:', result);
    });

    newSocket.on('area_transition', (transitionData) => {
      console.log('Area transition:', transitionData);
      // Update character position and area, clear maze (new game_state will follow)
      setGameState(prev => ({
        ...prev,
        character: {
          ...prev.character,
          current_area: transitionData.newArea,
          x_position: transitionData.x,
          y_position: transitionData.y,
          direction: transitionData.direction
        },
        maze: null, // Clear old maze  game_state event will follow with new maze
        nearbyPlayers: [] // Clear nearby players since we're in a new area
      }));
      // Request fresh online player list so fellowship invite panel
      // shows correct nearby players for the new area
      newSocket.emit('get_online_players');
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  };

  const disconnectFromGame = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setGameState({
        character: null,
        area: null,
        maze: null,
        nearbyPlayers: [],
        onlinePlayers: [],
        chatMessages: [],
        isConnected: false,
        fellowship: null,
        fellowshipBattle: null,
        fellowshipInvite: null,
      });
    }
  };

  const movePlayer = (direction) => {
    if (socket && gameState.isConnected) {
      const now = Date.now();
      if (now - moveThrottleRef.current < 150) return; // throttle: max ~7 moves/sec
      moveThrottleRef.current = now;
      socket.emit('move', direction);
    }
  };

  const performAction = (actionData) => {
    if (socket && gameState.isConnected) {
      socket.emit('action', actionData);
    }
  };

  const sendChatMessage = (message, overrideChatType, recipientId) => {
    if (socket && gameState.isConnected && message.trim()) {
      const payload = {
        message: message.trim(),
        chatType: overrideChatType || chatType
      };
      if (payload.chatType === 'whisper' && recipientId) {
        payload.recipientId = recipientId;
      }
      socket.emit('chat', payload);
    }
  };

  const requestOnlinePlayers = () => {
    if (socket && gameState.isConnected) {
      socket.emit('get_online_players');
    }
  };

  const targetPlayer = (targetData) => {
    if (socket && gameState.isConnected) {
      socket.emit('target_player', targetData);
    }
  };

  // Core fetch — called internally, protected against overlapping requests
  const _doRefreshCharacter = async () => {
    if (refreshInFlightRef.current) return; // skip if a fetch is already running
    const token = localStorage.getItem('token');
    if (!token) return;

    refreshInFlightRef.current = true;
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/game/character`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGameState(prev => ({
          ...prev,
          character: data.character
        }));
      }
    } catch (error) {
      console.error('Error refreshing character data:', error);
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  // Public API: debounced — multiple calls within 300ms coalesce into one fetch.
  // Returns a promise that resolves after the actual fetch completes, so callers
  // that `await refreshCharacterData()` still work correctly.
  const refreshCharacterData = useCallback(() => {
    return new Promise((resolve) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(async () => {
        await _doRefreshCharacter();
        resolve();
      }, 300);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Request a full game state refresh (character + maze + nearby players) via socket.
  // Use after death/resurrect or any area change to ensure maze is synced.
  const requestGameState = () => {
    if (socket && gameState.isConnected) {
      socket.emit('request_game_state');
    }
  };

  const dismissFellowshipInvite = () => {
    setGameState(prev => ({ ...prev, fellowshipInvite: null }));
  };

  const value = {
    gameState,
    setGameState,
    chatType,
    setChatType,
    movePlayer,
    performAction,
    sendChatMessage,
    targetPlayer,
    refreshCharacterData,
    requestGameState,
    requestOnlinePlayers,
    isConnected: gameState.isConnected,
    socket,
    dismissFellowshipInvite,
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};
